import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { CatalogModuleConfig } from '@/types/moduleConfig';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import type { QueryClient } from '@tanstack/react-query';
import { JsonFormsRenderer } from '@/components/JsonFormsRenderer';
import {
  useCatalogImageMetadata,
  useCreateEntity,
  useEntity,
  useEntityTypeWithSchemas,
  useUpdateEntity,
  useUploadCatalogImage,
} from '@/hooks';
import type { Entity } from '@/types/Entity';
import type { EntityActionNotifications } from '@/actions/entityActionRegistry';
import { queryKeys } from '@/lib/queryKeys';
import { catalogImageService } from '@/services/catalogImageService';
import {
  catalogImportService,
  type CatalogImportErrorDetail,
  type CatalogImportPayloadRow,
  type CatalogImportResponse,
} from '@/services/catalogImportService';
import {
  catalogAdjustmentService,
  type CatalogAdjustmentScope,
  type CatalogAdjustmentResponse,
} from '@/services/catalogAdjustmentService';
import { parseCatalogImportFile } from './catalogImportFile';
import { getErrorMessage, isApiError } from '@/utils/httpClient';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';

const CATALOG_IMPORT_TEMPLATE_PATH = '/templates/catalog-import-template.csv';
const CATALOG_IMPORT_DEMO_PATH = '/templates/catalog-import-demo.csv';

export type CatalogActionDialogState =
  | {
      type: 'create';
      queryClient: QueryClient;
      notifications?: EntityActionNotifications;
    }
  | {
      type: 'edit';
      entity: Entity;
      queryClient: QueryClient;
      notifications?: EntityActionNotifications;
    }
  | {
      type: 'import';
      queryClient: QueryClient;
      notifications?: EntityActionNotifications;
    };

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const normalizeCatalogFormData = (data: Record<string, unknown>): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {
    ...data,
  };

  // Ensure metadata is removed as it is no longer supported
  delete normalized.metadata;
  // Remove transient UI flags
  delete normalized._is_parent;

  const parentId = normalized.parent_id;
  normalized.parent_id =
    typeof parentId === 'string' && parentId.trim().length > 0 ? parentId : null;

  // Normalize LIN, Old LIN, and NSN (uppercase for consistency)
  // Note: lin uses "type": "string" (non-nullable) in the entity schema,
  // so we must omit the key rather than set null when the value is empty.
  // NSN is now nullable ("type": ["string", "null"]) to support PNP items.
  if (typeof normalized.lin === 'string') {
    const trimmed = normalized.lin.trim().toUpperCase();
    if (trimmed.length > 0) {
      normalized.lin = trimmed;
    } else {
      delete normalized.lin;
    }
  } else if (normalized.lin == null) {
    delete normalized.lin;
  }
  if (typeof normalized.old_lin === 'string') {
    const trimmed = normalized.old_lin.trim().toUpperCase();
    normalized.old_lin = trimmed.length > 0 ? trimmed : null;
  }
  if (typeof normalized.nsn === 'string') {
    const trimmed = normalized.nsn.trim().toUpperCase();
    normalized.nsn = trimmed.length > 0 ? trimmed : null;
  } else {
    normalized.nsn = null;
  }

  // Normalize Size and Color (trim, null if empty)
  if (typeof normalized.size === 'string') {
    const trimmed = normalized.size.trim();
    normalized.size = trimmed.length > 0 ? trimmed : null;
  } else if (normalized.size !== null && normalized.size !== undefined) {
    normalized.size = null;
  }
  if (typeof normalized.color === 'string') {
    const trimmed = normalized.color.trim();
    normalized.color = trimmed.length > 0 ? trimmed : null;
  } else if (normalized.color !== null && normalized.color !== undefined) {
    normalized.color = null;
  }

  if (typeof normalized.soc_tag === 'string') {
    const trimmed = normalized.soc_tag.trim().toLowerCase();
    if (!trimmed) {
      normalized.soc_tag = false;
    } else if (['false', 'f', '0', 'no', 'n'].includes(trimmed)) {
      normalized.soc_tag = false;
    } else {
      normalized.soc_tag = true;
    }
  } else if (normalized.soc_tag == null) {
    // soc_tag schema is "type": "boolean" (non-nullable); null is not allowed
    normalized.soc_tag = false;
  } else if (typeof normalized.soc_tag !== 'boolean') {
    normalized.soc_tag = Boolean(normalized.soc_tag);
  }

  // When type is 'soc buy program', sync soc_tag=true for backwards compat
  // with any existing queries that still filter by soc_tag.
  if (typeof normalized.type === 'string' && normalized.type === 'soc buy program') {
    normalized.soc_tag = true;
  }

  const normalizedType = typeof normalized.type === 'string' ? normalized.type.toLowerCase() : '';
  const isBuyProgramLike = normalizedType === 'buy program' || normalizedType === 'soc buy program';

  if (!isBuyProgramLike) {
    delete normalized.loa;
  } else if (typeof normalized.loa === 'string') {
    const trimmedLoa = normalized.loa.trim();
    normalized.loa = trimmedLoa.length > 0 ? trimmedLoa : null;
  } else if (normalized.loa !== null && normalized.loa !== undefined) {
    normalized.loa = null;
  }

  // Numeric fields
  // unit_price schema is "type": "number" (non-nullable); omit the key if empty/unparseable
  if (typeof normalized.unit_price === 'string') {
    const parsed = parseFloat(normalized.unit_price);
    if (isNaN(parsed)) {
      delete normalized.unit_price;
    } else {
      normalized.unit_price = parsed;
    }
  } else if (normalized.unit_price === null || normalized.unit_price === undefined) {
    delete normalized.unit_price;
  }

  // Integer threshold fields - must be provided together per database constraint
  const parseIntegerOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = typeof value === 'string' ? parseInt(value, 10) : Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  };

  if (Object.prototype.hasOwnProperty.call(normalized, 'storefront_minimum')) {
    const storefrontMinimum = parseIntegerOrNull(normalized.storefront_minimum);
    if (storefrontMinimum !== null) {
      normalized.storefront_minimum = storefrontMinimum;
    } else {
      delete normalized.storefront_minimum;
    }
  }

  const reorderThreshold = parseIntegerOrNull(normalized.reorder_threshold);
  const targetQuantity = parseIntegerOrNull(normalized.target_quantity);

  // Both must be set together or both null (enforced by DB constraint)
  if (reorderThreshold !== null && targetQuantity !== null) {
    normalized.reorder_threshold = reorderThreshold;
    normalized.target_quantity = targetQuantity;
  } else {
    // If one is missing, clear both to satisfy the paired constraint
    delete normalized.reorder_threshold;
    delete normalized.target_quantity;
  }

  // Alert percentage: optional integer 1-100, only meaningful when thresholds are set
  if (Object.prototype.hasOwnProperty.call(normalized, 'alert_percentage')) {
    const alertPct = parseIntegerOrNull(normalized.alert_percentage);
    if (alertPct !== null && alertPct >= 1 && alertPct <= 100) {
      normalized.alert_percentage = alertPct;
    } else {
      delete normalized.alert_percentage;
    }
  }

  return normalized;
};

const validateThresholds = (
  data: Record<string, unknown>,
): { valid: boolean; error: string | null } => {
  const reorder = data.reorder_threshold;
  const target = data.target_quantity;

  // If neither is set, that's fine
  if (
    (reorder === null || reorder === undefined || reorder === '') &&
    (target === null || target === undefined || target === '')
  ) {
    return { valid: true, error: null };
  }

  const reorderNum = typeof reorder === 'number' ? reorder : parseInt(String(reorder), 10);
  const targetNum = typeof target === 'number' ? target : parseInt(String(target), 10);

  // If one is set but not the other
  if (Number.isFinite(reorderNum) !== Number.isFinite(targetNum)) {
    return {
      valid: false,
      error: 'Both Reorder Threshold and Target Quantity must be set together, or both left empty.',
    };
  }

  // Both are set - check that target >= reorder
  if (Number.isFinite(reorderNum) && Number.isFinite(targetNum)) {
    if (targetNum < reorderNum) {
      return {
        valid: false,
        error: 'Target Quantity must be greater than or equal to Reorder Threshold.',
      };
    }
  }

  return { valid: true, error: null };
};

const useCatalogFormState = (initialData: Record<string, unknown>, resetKey?: string) => {
  const [formData, setFormData] = useState<Record<string, unknown>>(initialData);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    setFormData(initialData);
    setIsValid(false);
  }, [initialData, resetKey]);

  return {
    formData,
    setFormData,
    isValid,
    setIsValid,
  } as const;
};

const useCatalogSchemas = () => {
  const { data, isLoading, error } = useEntityTypeWithSchemas('catalog');
  return { config: data, isLoading, error } as const;
};

/**
 * Returns true when the catalog item name contains "NONSIZED" (case-insensitive).
 * NONSIZED items are allowed to use any location level as their default location,
 * bypassing the stockable-only restriction.
 */
const nameIsNonSized = (name: unknown): boolean => {
  if (typeof name !== 'string') return false;
  return name.toUpperCase().includes('NONSIZED');
};

const LOCATION_SCOPES = new Set([
  '#/properties/primary_location_id',
  '#/properties/secondary_location_id',
]);

/**
 * Recursively traverses a UI schema element and injects `is_stockable=eq.true`
 * into the dataSource params of primary/secondary location controls so that
 * only stockable hierarchy levels appear in the location picker dropdowns.
 */
const injectStockableFilter = (element: unknown): unknown => {
  if (!element || typeof element !== 'object') return element;
  const el = element as Record<string, unknown>;

  if (el.type === 'Control' && typeof el.scope === 'string' && LOCATION_SCOPES.has(el.scope)) {
    const opts = (
      typeof el.options === 'object' && el.options !== null ? el.options : {}
    ) as Record<string, unknown>;
    const ds = (
      typeof opts.dataSource === 'object' && opts.dataSource !== null ? opts.dataSource : {}
    ) as Record<string, unknown>;
    const params = (typeof ds.params === 'object' && ds.params !== null ? ds.params : {}) as Record<
      string,
      string
    >;
    return {
      ...el,
      options: {
        ...opts,
        dataSource: {
          ...ds,
          params: { ...params, is_stockable: 'eq.true' },
        },
      },
    };
  }

  if (Array.isArray(el.elements)) {
    return { ...el, elements: (el.elements as unknown[]).map(injectStockableFilter) };
  }

  return el;
};

interface CatalogFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  entity?: Entity;
  queryClient: QueryClient;
  notifications?: EntityActionNotifications;
  onClose: () => void;
}

const resettableClone = (source?: Record<string, unknown>): Record<string, unknown> =>
  source ? JSON.parse(JSON.stringify(source)) : {};

const buildIdentifier = (lin?: string) => {
  const suffix = Date.now().toString().slice(-6);
  return lin ? `CAT-${lin}-${suffix}` : `CAT-${Date.now()}`;
};

const invalidateCatalogQueries = async (queryClient: QueryClient, entityId?: string) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['entities', 'list', 'catalog'] }),
    queryClient.invalidateQueries({ queryKey: ['hierarchy'] }),
    queryClient.invalidateQueries({ queryKey: ['catalog'] }), // specific catalog keys if any
    entityId
      ? queryClient.invalidateQueries({ queryKey: queryKeys.entities.detail('catalog', entityId) })
      : Promise.resolve(),
  ]);
};

interface Catalog4949AdjustmentDialogProps {
  open: boolean;
  initialLin: string;
  initialNsn: string;
  initialNomenclature: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    lin: string;
    nsn: string;
    nomenclature: string;
    scope: CatalogAdjustmentScope;
    justification?: string;
  }) => void;
}

const Catalog4949AdjustmentDialog = ({
  open,
  initialLin,
  initialNsn,
  initialNomenclature,
  submitting,
  onClose,
  onSubmit,
}: Catalog4949AdjustmentDialogProps) => {
  const { config: catalogConfig } = useModuleConfig<CatalogModuleConfig>('catalog');
  const catalogLabels = catalogConfig.features.catalog;

  const adjustmentScopeOptions: Array<{
    value: CatalogAdjustmentScope;
    label: string;
    tooltip: string;
  }> = useMemo(
    () => [
      {
        value: 'available_only',
        label: catalogLabels.available_stock_label,
        tooltip: 'Updates assets with available status only.',
      },
      {
        value: 'assigned_only',
        label: catalogLabels.assigned_stock_label,
        tooltip: 'Updates assets with assigned status only.',
      },
      {
        value: 'both',
        label: 'Both',
        tooltip: 'Updates both available and assigned assets.',
      },
    ],
    [catalogLabels.available_stock_label, catalogLabels.assigned_stock_label],
  );

  const [lin, setLin] = useState(initialLin);
  const [nsn, setNsn] = useState(initialNsn);
  const [nomenclature, setNomenclature] = useState(initialNomenclature);
  const [scope, setScope] = useState<CatalogAdjustmentScope>('both');
  const [justification, setJustification] = useState('');

  useEffect(() => {
    if (!open) return;
    setLin(initialLin);
    setNsn(initialNsn);
    setNomenclature(initialNomenclature);
    setScope('both');
    setJustification('');
  }, [open, initialLin, initialNsn, initialNomenclature]);

  const canSubmit =
    lin.trim().length > 0 && nsn.trim().length > 0 && nomenclature.trim().length > 0;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>4949 Adjustment</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Update catalog LIN/NSN/Nomenclature and optionally propagate those values to stock
            and/or assigned assets.
          </Typography>
          <TextField
            label="LIN"
            value={lin}
            onChange={(event) => setLin(event.target.value.toUpperCase())}
          />
          <TextField
            label="NSN"
            value={nsn}
            onChange={(event) => setNsn(event.target.value.toUpperCase())}
          />
          <TextField
            label="Nomenclature"
            value={nomenclature}
            onChange={(event) => setNomenclature(event.target.value)}
          />

          <FormControl>
            <FormLabel>Adjustment Scope</FormLabel>
            <RadioGroup
              value={scope}
              onChange={(event) => setScope(event.target.value as CatalogAdjustmentScope)}
            >
              {adjustmentScopeOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2">{option.label}</Typography>
                      <Tooltip title={option.tooltip}>
                        <InfoIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            label="Justification"
            placeholder="Enter justification for DA 4949"
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            disabled={submitting}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <ViewerGuardedButton
          variant="contained"
          disabled={!canSubmit || submitting}
          onClick={() =>
            onSubmit({
              lin,
              nsn,
              nomenclature,
              scope,
              justification: justification.trim() || undefined,
            })
          }
        >
          {submitting ? 'Updating…' : 'Update'}
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};

interface CatalogAdjustmentSummaryDialogProps {
  open: boolean;
  response: CatalogAdjustmentResponse | null;
  onClose: () => void;
}

const CatalogAdjustmentSummaryDialog = ({
  open,
  response,
  onClose,
}: CatalogAdjustmentSummaryDialogProps) => {
  if (!response) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Adjustment Complete</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={0.75}>
          <Typography variant="body2">
            Updated stock records: {response.summary.updatedAvailableAssetCount}
          </Typography>
          <Typography variant="body2">
            Updated HR records: {response.summary.updatedAssignedAssetCount}
          </Typography>
          <Typography variant="body2">
            Total updated assets: {response.summary.updatedTotalAssetCount}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CatalogFormDialog = ({
  open,
  mode,
  entity,
  queryClient,
  notifications,
  onClose,
}: CatalogFormDialogProps) => {
  const { config, isLoading, error } = useCatalogSchemas();
  const createEntity = useCreateEntity();
  const updateEntity = useUpdateEntity();
  const isSubmitting = createEntity.isPending || updateEntity.isPending;

  const initialFormData = useMemo(() => {
    if (mode === 'edit' && entity) {
      const base = resettableClone(entity.entity_data);
      delete base.metadata;
      // If editing an existing item that has no parent, flag it as a parent item
      // so the UI schema can disable the parent selector to prevent accidental nesting changes.
      if (!base.parent_id) {
        base._is_parent = true;
      }
      // Seed the top-level entity status into form data so the Status control can display it
      base.status = entity.status;
      return base;
    }
    return {};
  }, [mode, entity]);
  const resetKey = `${mode}-${entity?.id ?? 'new'}-${open ? 'open' : 'closed'}`;

  const { formData, setFormData, isValid, setIsValid } = useCatalogFormState(
    initialFormData,
    resetKey,
  );

  const thresholdValidation = useMemo(() => validateThresholds(formData), [formData]);
  const isFormValid = isValid && thresholdValidation.valid;
  const isEditMode = mode === 'edit' && !!entity;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { data: imageMetadata } = useCatalogImageMetadata(isEditMode ? entity?.id : undefined);
  const uploadMutation = useUploadCatalogImage({
    onError: (error) => {
      notifications?.error('Image upload failed', getErrorMessage(error));
    },
  });
  const [catalogImageUrl, setCatalogImageUrl] = useState<string | null>(null);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [adjustmentSubmitting, setAdjustmentSubmitting] = useState(false);
  const [adjustmentSummary, setAdjustmentSummary] = useState<CatalogAdjustmentResponse | null>(
    null,
  );
  const [adjustmentSummaryOpen, setAdjustmentSummaryOpen] = useState(false);

  // Detect sized variant and fetch parent for expiration inheritance hint
  const parentId = entity?.entity_data?.parent_id as string | undefined;
  const isVariant = typeof parentId === 'string' && parentId.trim().length > 0;
  const { data: parentEntity } = useEntity(parentId ?? '', 'catalog');
  const inheritsExpiration = useMemo(() => {
    if (!isVariant || !isEditMode || !parentEntity) return false;
    const childExpStart = entity?.entity_data?.expiration_start_date;
    const childExpDuration = entity?.entity_data?.expiration_duration_from_start_date;
    const childHasExpiration = Boolean(childExpStart) || Boolean(childExpDuration);
    const parentExpStart = parentEntity.entity_data?.expiration_start_date;
    const parentExpDuration = parentEntity.entity_data?.expiration_duration_from_start_date;
    const parentHasExpiration = Boolean(parentExpStart) || Boolean(parentExpDuration);
    return !childHasExpiration && parentHasExpiration;
  }, [isVariant, isEditMode, entity, parentEntity]);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    if (!isEditMode || !entity?.id || !imageMetadata?.hasImage) {
      setCatalogImageUrl(null);
      return () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    }

    (async () => {
      try {
        const url = await catalogImageService.getImageObjectUrl(entity.id);
        if (!cancelled) {
          objectUrl = url;
          setCatalogImageUrl(url);
        }
      } catch {
        if (!cancelled) {
          setCatalogImageUrl(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [
    entity?.id,
    imageMetadata?.hasImage,
    imageMetadata?.fileName,
    imageMetadata?.fileSize,
    isEditMode,
  ]);

  const handleValidationChange = (valid: boolean) => {
    setIsValid(valid);
  };

  const handleFormDataChange = (next: Record<string, unknown>) => {
    setFormData(next ?? {});
  };

  const handleCatalog4949Adjustment = async (payload: {
    lin: string;
    nsn: string;
    nomenclature: string;
    scope: CatalogAdjustmentScope;
    justification?: string;
  }) => {
    if (!entity || !isEditMode) {
      return;
    }

    setAdjustmentSubmitting(true);
    const da4949Window = preOpenWindow();
    try {
      const response = await catalogAdjustmentService.applyAdjustment({
        catalogId: entity.id,
        lin: payload.lin,
        nsn: payload.nsn,
        nomenclature: payload.nomenclature,
        scope: payload.scope,
        justification: payload.justification,
      });

      if (response.da4949GeneratedDocumentId) {
        await openDocumentWithAuth(
          `/api/rest/app/v1/documents/${response.da4949GeneratedDocumentId}`,
          da4949Window,
        );
      } else if (da4949Window && !da4949Window.closed) {
        da4949Window.close();
      }

      setFormData((prev) => ({
        ...prev,
        lin: payload.lin,
        nsn: payload.nsn,
        name: payload.nomenclature,
      }));

      await invalidateCatalogQueries(queryClient, entity.id);

      setAdjustmentDialogOpen(false);
      setAdjustmentSummary(response);
      setAdjustmentSummaryOpen(true);
      notifications?.success(
        '4949 Adjustment complete',
        `${response.summary.updatedTotalAssetCount} asset record(s) updated.`,
      );
    } catch (error) {
      if (da4949Window && !da4949Window.closed) {
        da4949Window.close();
      }
      notifications?.error('4949 Adjustment failed', getErrorMessage(error));
    } finally {
      setAdjustmentSubmitting(false);
    }
  };

  const effectiveUiSchema = useMemo(() => {
    if (!config?.ui_schema) return undefined;
    if (!isEditMode) return config.ui_schema;
    return config.ui_schema_edit ?? config.ui_schema;
  }, [config?.ui_schema, config?.ui_schema_edit, isEditMode]);

  // Restrict location pickers to stockable levels only, unless this item is NONSIZED.
  // NONSIZED items (case-insensitive) may use any location level as their default location.
  const resolvedUiSchema = useMemo(() => {
    if (!effectiveUiSchema) return undefined;
    if (nameIsNonSized(formData.name)) return effectiveUiSchema;
    return injectStockableFilter(effectiveUiSchema) as typeof effectiveUiSchema;
  }, [effectiveUiSchema, formData.name]);

  const dialogTitle = mode === 'edit' ? 'Edit Catalog Item' : 'Create Catalog Item';

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) {
      notifications?.error('Validation error', 'Please resolve the highlighted fields.');
      return;
    }

    try {
      if (mode === 'edit' && entity) {
        const displayName =
          typeof formData.name === 'string' && formData.name.length > 0
            ? String(formData.name)
            : entity.display_name;
        const normalized = normalizeCatalogFormData(formData);

        // Extract status from form data to update the top-level entity status
        const newStatus = typeof normalized.status === 'string' ? normalized.status : undefined;
        delete normalized.status;

        await updateEntity.mutateAsync({
          type: 'catalog',
          id: entity.id,
          patch: {
            entity_data: normalized,
            display_name: displayName,
            ...(newStatus ? { status: newStatus } : {}),
          },
        });
        notifications?.success('Catalog item updated', `${displayName} saved successfully.`);

        await invalidateCatalogQueries(queryClient, entity.id);
      } else {
        const name =
          typeof formData.name === 'string' && formData.name.length > 0
            ? String(formData.name)
            : 'New Catalog Item';
        const normalized = normalizeCatalogFormData(formData);
        const lin = typeof normalized.lin === 'string' ? normalized.lin : undefined;

        await createEntity.mutateAsync({
          entity_type: 'catalog',
          entity_identifier: buildIdentifier(lin),
          display_name: name,
          entity_data: normalized,
          status: 'active',
        });
        notifications?.success('Catalog item created', `${name} added successfully.`);
        await invalidateCatalogQueries(queryClient);
      }
      onClose();
    } catch (submitError) {
      notifications?.error('Action failed', getErrorMessage(submitError));
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {error && <Alert severity="error">Failed to load form schema.</Alert>}
          {thresholdValidation.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {thresholdValidation.error}
            </Alert>
          )}
          {!isLoading && !error && config?.entity_schema && (
            <>
              {inheritsExpiration && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Expiration settings are currently inherited from the parent catalog item. Setting
                  values here will override the inherited configuration.
                </Alert>
              )}
              <JsonFormsRenderer
                schema={config.entity_schema}
                uiSchema={resolvedUiSchema}
                data={formData}
                onChange={handleFormDataChange}
                onValidationChange={handleValidationChange}
              />
              {isEditMode && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Catalog Image
                  </Typography>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                        <Box sx={{ flex: '0 0 auto' }}>
                          {imageMetadata?.hasImage && catalogImageUrl ? (
                            <Box
                              component="img"
                              src={catalogImageUrl}
                              alt={imageMetadata.fileName ?? 'Catalog image'}
                              sx={{
                                maxWidth: 240,
                                maxHeight: 240,
                                borderRadius: 1,
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                objectFit: 'contain',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 240,
                                height: 240,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1,
                                border: (theme) => `1px dashed ${theme.palette.divider}`,
                                color: 'text.secondary',
                                fontSize: 14,
                              }}
                            >
                              No image uploaded
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ flex: '1 1 auto' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Upload a JPEG or PNG thumbnail image for this catalog item (max 5MB).
                          </Typography>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png"
                            style={{ display: 'none' }}
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              // Reset so re-selecting the same file after a failed attempt still fires change.
                              event.target.value = '';
                              if (!file || !entity?.id) return;
                              uploadMutation.mutate({ catalogId: entity.id, file });
                            }}
                          />
                          <ViewerGuardedButton
                            variant="contained"
                            size="small"
                            onClick={() => {
                              fileInputRef.current?.click();
                            }}
                            disabled={uploadMutation.isPending}
                          >
                            {imageMetadata?.hasImage ? 'Replace Image' : 'Upload Image'}
                          </ViewerGuardedButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {isEditMode && (
            <ViewerGuardedButton
              variant="outlined"
              disabled={isSubmitting}
              onClick={() => setAdjustmentDialogOpen(true)}
            >
              4949 Adjustment
            </ViewerGuardedButton>
          )}
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <ViewerGuardedButton
            onClick={() => void handleSubmit()}
            variant="contained"
            disabled={!isFormValid || isSubmitting || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? 'Uploading image…' : isSubmitting ? 'Saving…' : 'Save'}
          </ViewerGuardedButton>
        </DialogActions>
      </Dialog>

      {isEditMode && entity ? (
        <Catalog4949AdjustmentDialog
          open={adjustmentDialogOpen}
          initialLin={typeof formData.lin === 'string' ? formData.lin : ''}
          initialNsn={typeof formData.nsn === 'string' ? formData.nsn : ''}
          initialNomenclature={
            typeof formData.name === 'string' && formData.name.length > 0
              ? formData.name
              : entity.display_name
          }
          submitting={adjustmentSubmitting}
          onClose={() => setAdjustmentDialogOpen(false)}
          onSubmit={(payload) => void handleCatalog4949Adjustment(payload)}
        />
      ) : null}

      <CatalogAdjustmentSummaryDialog
        open={adjustmentSummaryOpen}
        response={adjustmentSummary}
        onClose={() => setAdjustmentSummaryOpen(false)}
      />
    </>
  );
};

const getImportErrorDetails = (error: unknown): CatalogImportErrorDetail[] => {
  if (!isApiError(error)) {
    return [];
  }

  const details = error.details;
  if (Array.isArray(details)) {
    return details
      .filter((detail): detail is CatalogImportErrorDetail => isRecord(detail))
      .map((detail) => ({
        rowNumber:
          typeof detail.rowNumber === 'number' || detail.rowNumber === null
            ? detail.rowNumber
            : null,
        column: typeof detail.column === 'string' || detail.column === null ? detail.column : null,
        message: typeof detail.message === 'string' ? detail.message : 'Validation error.',
      }));
  }

  if (isRecord(details) && Array.isArray(details.details)) {
    return details.details
      .filter((detail): detail is CatalogImportErrorDetail => isRecord(detail))
      .map((detail) => ({
        rowNumber:
          typeof detail.rowNumber === 'number' || detail.rowNumber === null
            ? detail.rowNumber
            : null,
        column: typeof detail.column === 'string' || detail.column === null ? detail.column : null,
        message: typeof detail.message === 'string' ? detail.message : 'Validation error.',
      }));
  }

  return [];
};

const escapeCsvCell = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const buildValidationErrorCsv = (errors: CatalogImportErrorDetail[]): string => {
  const header = 'row,column,message';
  const lines = errors.map((detail) => {
    const row = detail.rowNumber === null ? '' : String(detail.rowNumber);
    const column = detail.column ?? '';
    const message = detail.message;
    return [row, column, message].map(escapeCsvCell).join(',');
  });
  return [header, ...lines].join('\n');
};

const downloadCsv = (content: string, fileName: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
};

interface CatalogImportDialogProps {
  open: boolean;
  queryClient: QueryClient;
  notifications?: EntityActionNotifications;
  onClose: () => void;
}

const CatalogImportDialog = ({
  open,
  queryClient,
  notifications,
  onClose,
}: CatalogImportDialogProps) => {
  const [rows, setRows] = useState<CatalogImportPayloadRow[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<CatalogImportErrorDetail[]>([]);
  const [validationResponse, setValidationResponse] = useState<CatalogImportResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) {
      setRows([]);
      setFileName(null);
      setIsParsingFile(false);
      setIsValidating(false);
      setIsImporting(false);
      setErrorMessage(null);
      setValidationErrors([]);
      setValidationResponse(null);
    }
  }, [open]);

  const handleSelectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsParsingFile(true);
    setErrorMessage(null);
    setValidationErrors([]);
    setValidationResponse(null);

    try {
      const parsed = await parseCatalogImportFile(file);
      setRows(parsed.rows);
      setFileName(parsed.fileName);
      notifications?.info(
        'File Loaded',
        `Loaded ${parsed.rows.length} row${parsed.rows.length === 1 ? '' : 's'} from ${parsed.fileName}.`,
      );
    } catch (error) {
      setRows([]);
      setFileName(null);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsParsingFile(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleValidate = async () => {
    if (rows.length === 0) return;

    setIsValidating(true);
    setErrorMessage(null);
    setValidationErrors([]);
    setValidationResponse(null);

    try {
      const response = await catalogImportService.validateImport(rows);
      setValidationResponse(response);
      notifications?.success(
        'Validation Passed',
        `${response.summary.totalRows} rows validated. ${response.summary.createCount} create, ${response.summary.updateCount} update.`,
      );
    } catch (error) {
      const message = getErrorMessage(error);
      const details = getImportErrorDetails(error);
      setErrorMessage(message);
      setValidationErrors(details);
      notifications?.error('Validation Failed', message);
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (rows.length === 0 || !validationResponse || validationErrors.length > 0) {
      return;
    }

    setIsImporting(true);
    setErrorMessage(null);

    try {
      const response = await catalogImportService.applyImport(rows);
      await invalidateCatalogQueries(queryClient);
      notifications?.success(
        'Import Complete',
        `${response.summary.totalRows} rows imported. ${response.summary.createCount} created, ${response.summary.updateCount} updated.`,
      );
      onClose();
    } catch (error) {
      const message = getErrorMessage(error);
      const details = getImportErrorDetails(error);
      setErrorMessage(message);
      setValidationErrors(details);
      notifications?.error('Import Failed', message);
    } finally {
      setIsImporting(false);
    }
  };

  const hasValidation = Boolean(validationResponse);
  const canValidate = rows.length > 0 && !isParsingFile && !isValidating && !isImporting;
  const canImport = hasValidation && validationErrors.length === 0 && !isImporting && !isValidating;
  const previewRows = validationResponse?.preview ?? [];
  const previewRowsToDisplay = previewRows.slice(0, 5);
  const hasPreviewRows = previewRowsToDisplay.length > 0;
  const validationErrorsCsv =
    validationErrors.length > 0 ? buildValidationErrorCsv(validationErrors) : null;

  const handleDownloadValidationErrors = () => {
    if (!validationErrorsCsv) {
      return;
    }
    downloadCsv(validationErrorsCsv, 'catalog-import-validation-errors.csv');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Catalog Data</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a catalog spreadsheet and validate before applying. Required fields per row: LIN,
          NSN, and Nomenclature. Import is strict all-or-nothing.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Validate runs a dry-run first so you can sanity-check planned create vs update results
          before applying the import.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={(event) => void handleSelectFile(event)}
          />
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            disabled={isParsingFile || isValidating || isImporting}
          >
            {isParsingFile ? 'Loading…' : fileName ? 'Choose Another File' : 'Choose File'}
          </Button>
          <Button
            variant="outlined"
            component="a"
            href={CATALOG_IMPORT_TEMPLATE_PATH}
            download="catalog-import-template.csv"
          >
            Download Template
          </Button>
          {fileName && (
            <Typography variant="body2" color="text.secondary">
              {fileName} ({rows.length} rows)
            </Typography>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Demo file for walkthroughs:{' '}
          <Typography
            component="a"
            variant="caption"
            href={CATALOG_IMPORT_DEMO_PATH}
            download="catalog-import-demo.csv"
            sx={{ color: 'text.secondary', textDecoration: 'underline' }}
          >
            Sample import CSV
          </Typography>
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {hasValidation && validationResponse && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Validation complete: {validationResponse.summary.totalRows} rows,{' '}
            {validationResponse.summary.createCount} create,{' '}
            {validationResponse.summary.updateCount} update.
          </Alert>
        )}

        {hasValidation && hasPreviewRows && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Row interpretation preview
            </Typography>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 72 }}>Row</TableCell>
                  <TableCell sx={{ width: 120 }}>Operation</TableCell>
                  <TableCell>Identifier</TableCell>
                  <TableCell>Display Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRowsToDisplay.map((previewRow) => (
                  <TableRow key={`${previewRow.rowNumber}-${previewRow.entityId}`}>
                    <TableCell>{previewRow.rowNumber}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {previewRow.operation}
                    </TableCell>
                    <TableCell>{previewRow.entityIdentifier}</TableCell>
                    <TableCell>{previewRow.displayName}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {previewRows.length > previewRowsToDisplay.length && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Showing first {previewRowsToDisplay.length} of {previewRows.length} rows.
              </Typography>
            )}
          </Box>
        )}

        {validationErrors.length > 0 && (
          <>
            <Alert severity="error" sx={{ mb: 1 }}>
              Validation returned {validationErrors.length} error
              {validationErrors.length === 1 ? '' : 's'}.
            </Alert>
            <Box sx={{ mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDownloadValidationErrors}
                disabled={isValidating || isImporting || !validationErrorsCsv}
              >
                Download Errors CSV
              </Button>
            </Box>
            <List dense sx={{ maxHeight: 280, overflowY: 'auto', mb: 1 }}>
              {validationErrors.slice(0, 25).map((detail, index) => {
                const rowLabel = detail.rowNumber ? `Row ${detail.rowNumber}` : 'General';
                const columnLabel = detail.column ? `, ${detail.column}` : '';
                return (
                  <ListItem key={`${detail.rowNumber ?? 'x'}-${detail.column ?? 'y'}-${index}`}>
                    <ListItemText primary={`${rowLabel}${columnLabel}: ${detail.message}`} />
                  </ListItem>
                );
              })}
            </List>
            {validationErrors.length > 25 && (
              <Typography variant="caption" color="text.secondary">
                Showing first 25 errors.
              </Typography>
            )}
          </>
        )}

        {(isValidating || isImporting) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                {isValidating ? 'Validating import…' : 'Applying import…'}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isParsingFile || isValidating || isImporting}>
          Cancel
        </Button>
        <ViewerGuardedButton onClick={() => void handleValidate()} disabled={!canValidate}>
          {isValidating ? 'Validating…' : 'Validate'}
        </ViewerGuardedButton>
        <ViewerGuardedButton
          variant="contained"
          onClick={() => void handleImport()}
          disabled={!canImport}
        >
          {isImporting ? 'Importing…' : 'Import'}
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};

export interface CatalogActionDialogsProps {
  state: CatalogActionDialogState | null;
  onClose: () => void;
}

export const CatalogActionDialogs = ({ state, onClose }: CatalogActionDialogsProps) => {
  if (!state) {
    return null;
  }

  if (state.type === 'create') {
    return (
      <CatalogFormDialog
        open
        mode="create"
        queryClient={state.queryClient}
        notifications={state.notifications}
        onClose={onClose}
      />
    );
  }

  if (state.type === 'edit') {
    return (
      <CatalogFormDialog
        open
        mode="edit"
        entity={state.entity}
        queryClient={state.queryClient}
        notifications={state.notifications}
        onClose={onClose}
      />
    );
  }

  if (state.type === 'import') {
    return (
      <CatalogImportDialog
        open
        queryClient={state.queryClient}
        notifications={state.notifications}
        onClose={onClose}
      />
    );
  }

  return null;
};
